import { AlertCircle, Copy, AlertTriangle, TrendingUp, MinusCircle, type LucideIcon } from 'lucide-react';
import { RULE_LABELS, type CreditNoteRule, type CreditNoteRuleTile, type CreditNoteExclusion } from '../../types/creditNotes';
import { formatCount } from '../../utils/creditNoteFormat';

/**
 * The five rules as tiles, and the tiles as the primary filter.
 *
 * WHAT A TILE SHOWS IS WHAT ITS FILTER RETURNS. `fired` and `?rule=<code>` come from one
 * evaluation on the server and are the same number by construction. That is the single
 * invariant of this component: it must never compute, adjust or combine a count of its own. A
 * tile whose number the click cannot reproduce is exactly the kind of quiet disagreement this
 * page was built to find in the ERP, and building one into the page would be inexcusable.
 *
 * NO COMBINED FLAG (decision 4). There is no score, no traffic light, no "worst rule". Stefan
 * has to see WHICH rule fired, so a document that trips three rules appears under all three,
 * and the tile numbers do not add up to the list length. That is correct, and the footnote
 * says so rather than leaving the reader to discover it by subtraction.
 *
 * `notEvaluated` IS SHOWN, NEVER FOLDED IN. A rule that could not judge a document has not
 * cleared it. Those documents keep their place in the list and carry a named reason in the
 * drawer; here they are a muted third number so the reader can see the rule's blind spot at
 * the same glance as its findings.
 */

const RULE_ICON: Record<CreditNoteRule, { icon: LucideIcon; cls: string }> = {
    NO_ITEM_LINE: { icon: AlertCircle, cls: 'text-red-600' },
    DUPLICATE: { icon: Copy, cls: 'text-red-600' },
    PRICE_DEVIATION: { icon: AlertTriangle, cls: 'text-amber-600' },
    LARGE_VS_SALES: { icon: TrendingUp, cls: 'text-amber-600' },
    QTY_NO_VALUE: { icon: MinusCircle, cls: 'text-gray-400' },
};

interface Props {
    tiles: CreditNoteRuleTile[];
    exclusions: CreditNoteExclusion[];
    documentsInWindow: number;
    activeRule: string;
    onSelect: (rule: string) => void;
}

/**
 * The thresholds a tile is running at, in one short phrase.
 *
 * A single group default reads as "> 100%". Where one company overrides it — bin runs
 * LARGE_VS_SALES at 100 % against the group's 50 % — both numbers are shown, because a tile
 * that prints one threshold while counting on two is lying quietly.
 */
function thresholdHint(tile: CreditNoteRuleTile): string | null {
    const values = tile.enabledFor
        .map(c => tile.thresholds[c])
        .filter(t => t && t.value !== null);
    if (values.length === 0) return null;
    const unit = values[0].unit === 'DAYS' ? ' d' : '%';
    const distinct = [...new Set(values.map(v => v.value))].sort((a, b) => (a ?? 0) - (b ?? 0));
    if (distinct.length === 1) {
        return unit === ' d' ? `${distinct[0]} d` : `> ${distinct[0]}%`;
    }
    return unit === ' d'
        ? `${distinct.join(' / ')} d`
        : `> ${distinct.join(' / ')}%`;
}

export function RuleTiles({ tiles, exclusions, documentsInWindow, activeRule, onSelect }: Props) {
    const excludedTotal = exclusions.reduce((sum, e) => sum + e.documents, 0);

    return (
        <div>
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() => onSelect('')}
                    className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm transition-colors ${
                        activeRule === '' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                >
                    <span className="text-gray-700">All credit notes</span>
                    <span className="font-semibold text-gray-900">{formatCount(documentsInWindow)}</span>
                </button>

                {tiles.map(tile => {
                    const style = RULE_ICON[tile.ruleCode];
                    const Icon = style.icon;
                    const active = activeRule === tile.ruleCode;
                    const hint = thresholdHint(tile);
                    return (
                        <button
                            key={tile.ruleCode}
                            onClick={() => onSelect(active ? '' : tile.ruleCode)}
                            title={
                                `${formatCount(tile.fired)} fired`
                                + (tile.wouldFire > 0 ? ` · ${formatCount(tile.wouldFire)} would fire if the rule were switched on for ${tile.disabledFor.join(', ')}` : '')
                                + (tile.notEvaluated > 0 ? ` · ${formatCount(tile.notEvaluated)} could not be judged` : '')
                            }
                            className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm transition-colors ${
                                active ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                        >
                            <Icon className={`h-4 w-4 ${style.cls}`} />
                            <span className="text-gray-700">{RULE_LABELS[tile.ruleCode]}</span>
                            <span className="font-semibold text-gray-900">{formatCount(tile.fired)}</span>
                            {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
                            {/* Switched off somewhere: what the reader is NOT being shown. */}
                            {tile.wouldFire > 0 && (
                                <span className="text-[11px] text-amber-600">+{formatCount(tile.wouldFire)} off</span>
                            )}
                            {tile.notEvaluated > 0 && (
                                <span className="text-[11px] text-gray-400">{formatCount(tile.notEvaluated)} n/a</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <p className="mt-2 text-[11px] leading-4 text-gray-500">
                Tiles are single-select, and each one returns exactly the documents it counts. A credit note
                that trips several rules appears under each of them, so the tiles do not add up to the list —
                there is deliberately no combined flag.
                {' '}<span className="text-gray-400">n/a</span> counts documents a rule could not judge; the
                drawer names the reason for each. <span className="text-amber-600">+n off</span> is what
                switching the rule on for the remaining companies would add.
                {excludedTotal > 0 && (
                    <>
                        {' '}Always excluded and named:{' '}
                        {exclusions.map((e, i) => (
                            <span key={e.code} title={e.documentNos.slice(0, 40).join(', ')}>
                                {i > 0 && ' · '}
                                {formatCount(e.documents)} {e.label}
                            </span>
                        ))}.
                    </>
                )}
            </p>
        </div>
    );
}
